import { useQuery } from '@tanstack/react-query';
import { api, type InvokeOutcome } from '../lib/api';

/**
 * Market/account data for the workstation tiles comes from the user's MCP connector (haipa) through the
 * same Execute path the chat uses (`POST /v1/connectors/:name/invoke`, read-only tools only). The server
 * returns JSON as text; these helpers parse the shapes the haipa tools emit (numbers arrive as strings).
 */
export interface Candle {
  t: number; // ms epoch
  o: number;
  h: number;
  l: number;
  c: number;
  v: number;
}
export interface ChartData {
  coin: string;
  interval: string;
  candles: Candle[];
}
export interface Position {
  coin: string;
  size: number;
  entry_px: number;
  position_value: number;
  unrealized_pnl: number;
  leverage: number;
  liquidation_px: number | null;
  margin_used: number;
}
export interface AccountData {
  account_value: number;
  total_unrealized_pnl: number;
  total_margin_used: number;
  withdrawable: number;
  positions: Position[];
}
export interface OpenOrder {
  coin: string;
  side: string;
  limit_px?: number;
  size?: number;
  original_size?: number;
  oid?: number | string;
  order_type?: string;
  reduce_only?: boolean;
  trigger?: unknown;
  [k: string]: unknown;
}
export interface Trade {
  coin: string;
  side: string;
  dir?: string;
  size: number;
  price: number;
  closed_pnl: number;
  fee: number;
  time: number;
  oid?: number | string;
}

export class InvokeFailed extends Error {}

const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
};
const numOrNull = (v: unknown): number | null =>
  v === null || v === undefined || v === '' ? null : num(v);

/** Unwraps an Execute outcome into the tool's JSON payload, or throws a readable error. */
export function unwrap(outcome: InvokeOutcome): unknown {
  if (outcome.status === 'denied') throw new InvokeFailed(`denied: ${outcome.reason}`);
  if (outcome.status === 'failed') throw new InvokeFailed(outcome.error);
  const r = outcome.result;
  if (r.isError) throw new InvokeFailed(r.text.slice(0, 300) || 'tool error');
  if (r.structured !== undefined && r.structured !== null) return r.structured;
  try {
    return JSON.parse(r.text);
  } catch {
    throw new InvokeFailed(`unexpected reply: ${r.text.slice(0, 120)}`);
  }
}

export const parseChart = (raw: unknown): ChartData => {
  const j = (raw ?? {}) as { coin?: string; interval?: string; candles?: unknown[] };
  const candles = (j.candles ?? []).map((c) => {
    const x = c as Record<string, unknown>;
    return { t: num(x.t), o: num(x.o), h: num(x.h), l: num(x.l), c: num(x.c), v: num(x.v) };
  });
  candles.sort((a, b) => a.t - b.t);
  return { coin: j.coin ?? '', interval: j.interval ?? '', candles };
};

export const parseAccount = (raw: unknown): AccountData => {
  const j = (raw ?? {}) as Record<string, unknown>;
  const positions = ((j.positions as unknown[]) ?? []).map((p) => {
    const x = p as Record<string, unknown>;
    return {
      coin: String(x.coin ?? ''),
      size: num(x.size),
      entry_px: num(x.entry_px),
      position_value: num(x.position_value),
      unrealized_pnl: num(x.unrealized_pnl),
      leverage: num(x.leverage),
      liquidation_px: numOrNull(x.liquidation_px),
      margin_used: num(x.margin_used),
    };
  });
  return {
    account_value: num(j.account_value ?? j.total_account_value_usd),
    total_unrealized_pnl: num(j.total_unrealized_pnl),
    total_margin_used: num(j.total_margin_used),
    withdrawable: num(j.withdrawable),
    positions,
  };
};

export const parseOrders = (raw: unknown): OpenOrder[] => {
  const j = (raw ?? {}) as { orders?: unknown[] };
  return (j.orders ?? []).map((o) => {
    const x = o as Record<string, unknown>;
    return {
      ...x,
      coin: String(x.coin ?? ''),
      side: String(x.side ?? ''),
      limit_px: num(x.limit_px ?? x.price),
      size: num(x.size ?? x.sz),
      original_size: num(x.original_size ?? x.orig_sz ?? x.size),
      order_type: String(x.order_type ?? x.type ?? ''),
    };
  });
};

export const parseTrades = (raw: unknown): Trade[] => {
  const j = (raw ?? {}) as { trades?: unknown[] };
  return (j.trades ?? []).map((t) => {
    const x = t as Record<string, unknown>;
    return {
      coin: String(x.coin ?? ''),
      side: String(x.side ?? ''),
      ...(x.dir ? { dir: String(x.dir) } : {}),
      size: num(x.size),
      price: num(x.price),
      closed_pnl: num(x.closed_pnl),
      fee: num(x.fee),
      time: num(x.time),
      ...(x.oid !== undefined ? { oid: x.oid as number | string } : {}),
    };
  });
};

const invokeJson = async (connector: string, tool: string, args: Record<string, unknown>) =>
  unwrap(await api.invoke(connector, tool, args));

/** The connector the market tiles read from: the first active one unless the tile names another. */
export function useConnectors() {
  return useQuery({ queryKey: ['connectors'], queryFn: api.connectors, staleTime: 60_000 });
}

export function useChart(connector: string | undefined, coin: string, interval: string) {
  return useQuery({
    queryKey: ['ws', 'chart', connector, coin, interval],
    queryFn: async () =>
      parseChart(await invokeJson(connector as string, 'get_chart', { coin, interval })),
    enabled: Boolean(connector && coin),
    refetchInterval: 60_000,
    retry: 1,
  });
}

export function useAccount(connector: string | undefined) {
  return useQuery({
    queryKey: ['ws', 'account', connector],
    queryFn: async () => parseAccount(await invokeJson(connector as string, 'get_account', {})),
    enabled: Boolean(connector),
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useOpenOrders(connector: string | undefined) {
  return useQuery({
    queryKey: ['ws', 'orders', connector],
    queryFn: async () => parseOrders(await invokeJson(connector as string, 'get_open_orders', {})),
    enabled: Boolean(connector),
    refetchInterval: 30_000,
    retry: 1,
  });
}

export function useRecentTrades(connector: string | undefined, limit = 25) {
  return useQuery({
    queryKey: ['ws', 'trades', connector, limit],
    queryFn: async () =>
      parseTrades(await invokeJson(connector as string, 'get_recent_trades', { limit })),
    enabled: Boolean(connector),
    refetchInterval: 60_000,
    retry: 1,
  });
}

export const fmt = {
  px: (n: number) =>
    n >= 1000
      ? n.toLocaleString(undefined, { maximumFractionDigits: 2 })
      : n.toPrecision(6).replace(/\.?0+$/, ''),
  qty: (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 6 }),
  usd: (n: number) =>
    `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
  time: (ms: number) => new Date(ms).toLocaleString(),
};
